import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { useEffect, useState } from 'react';

import { DrawerContentLayout } from '../../cdl/Drawer/DrawerContentLayout';
import { useDrawer } from '../../cdl/Drawer/useDrawer.hook';
import { translate } from '../../common/helpers/translate.helper';
import { useLocalStorageState } from '../../common/hooks/useLocalStorageState';
import { Box } from '../../common/ui/Box';

import { CopyTokenButton } from './CopyTokenButton';
import { DevFlags } from './DevFlags';
import { DevToolsIconButton } from './DevToolsIconButton';
import { FeatureFlags } from './FeatureFlags';
import { ResizableDevtoolsPanel } from './ResizableDevtoolsPanel';
import { TanStackDevtoolsButtons } from './TanStackDevtoolsPanels';

interface DevToolsDrawerContentProps {
    onOpenQueryDevtools: () => void;
    onOpenRouterDevtools: () => void;
}

const DevToolsDrawerContent = (props: DevToolsDrawerContentProps) => {
    const isDevelopment = import.meta.env.MODE === 'development';

    return (
        <DrawerContentLayout>
            <Box display="flex" flexDirection="column" rowGap={40}>
                <Box display="flex" flexDirection="column" justifyContent="space-between" gap={24}>
                    <CopyTokenButton />
                    <DevFlags />
                </Box>
                <FeatureFlags />
                {isDevelopment ? (
                    <TanStackDevtoolsButtons
                        onOpenQueryDevtools={props.onOpenQueryDevtools}
                        onOpenRouterDevtools={props.onOpenRouterDevtools}
                    />
                ) : null}
            </Box>
        </DrawerContentLayout>
    );
};

declare global {
    interface Window {
        toggleToolbox: () => void;
    }
}

type ActivePanel = 'query' | 'router' | null;

export const Toolbox = () => {
    const [activePanel, setActivePanel] = useState<ActivePanel>(null);
    const [showToolbox, setShowToolbox] = useLocalStorageState('dev-tools', false);
    const { push, hasLayers, pop } = useDrawer();

    useEffect(() => {
        if (window.location.search.includes('dev-tools=false')) {
            setShowToolbox(false);
        }

        if (window.location.search.includes('dev-tools=true')) {
            setShowToolbox(true);
        }

        window.toggleToolbox = () => setShowToolbox((v) => !v);
    }, [setShowToolbox]);

    if (!showToolbox) {
        return null;
    }

    const handleClosePanel = () => {
        setActivePanel(null);
    };

    const openDrawer = () => {
        const handleOpenQueryDevtools = () => {
            setActivePanel('query');
            pop(drawerId);
        };

        const handleOpenRouterDevtools = () => {
            setActivePanel('router');
            pop(drawerId);
        };

        const drawerId = push({
            header: { title: translate('devTools.title') },
            content: (
                <DevToolsDrawerContent
                    onOpenQueryDevtools={handleOpenQueryDevtools}
                    onOpenRouterDevtools={handleOpenRouterDevtools}
                />
            ),
            width: '500px',
        });
    };

    return (
        <>
            {hasLayers ? null : <DevToolsIconButton onClick={openDrawer} />}
            {activePanel ? (
                <ResizableDevtoolsPanel onClose={handleClosePanel}>
                    {activePanel === 'query' ? <ReactQueryDevtoolsPanel onClose={handleClosePanel} /> : null}
                    {activePanel === 'router' ? <TanStackRouterDevtoolsPanel /> : null}
                </ResizableDevtoolsPanel>
            ) : null}
        </>
    );
};
